import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import ObjectsDropdown from '../Dropdowns/ObjectsDropdown';
import { LIKERT_SCALE, LIKERT_SCALE_ALTERNATE } from './chartColors';
import LikertHeatmap, {
  AllComparisonMode,
  COLOR_DELTA_FROM_ALL,
  COLOR_DELTA_FROM_ALL_ALTERNATE,
  COLOR_FAVOURABLE,
  COLOR_FAVOURABLE_ALTERNATE,
  COLOR_NEUTRAL,
  COLOR_SCALE_ALTERNATE,
  COLOR_UNFAVORABLE,
  COLOR_UNFAVORABLE_ALTERNATE,
  FORMAT_VALUE_PERCENTAGES_FAVOURABLE,
  FORMAT_VALUE_PERCENTAGES_NEUTRAL,
  FORMAT_VALUE_PERCENTAGES_UNFAVORABLE,
} from './LikertHeatmap';

export interface Props {
  data: Array<object>;
  reportItemId?: number;
  renderItem: (data: object) => JSX.Element;
}

const LikertHeatmapVisualizations: React.FC<Props> = (props) => {
  const { formatMessage } = useIntl();

  const LIKERT_HEATMAP_VISUALIZATIONS = useMemo(
    () => [
      {
        id: 'favorable_percentage',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.likert_visualization.favorable_percentage',
          defaultMessage: '% Favorable',
        }),
        colorScale: COLOR_FAVOURABLE,
        colorScaleAlternate: COLOR_FAVOURABLE_ALTERNATE,
        colorScaleDelta: COLOR_DELTA_FROM_ALL,
        colorScaleDeltaAlternate: COLOR_DELTA_FROM_ALL_ALTERNATE,
        ratingScale: LIKERT_SCALE,
        ratingScaleAlternate: LIKERT_SCALE_ALTERNATE,
        formatValue: FORMAT_VALUE_PERCENTAGES_FAVOURABLE,
        unit: '%',
      },
      {
        id: 'unfavorable_percentage',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.likert_visualization.unfavorable_percentage',
          defaultMessage: '% Unfavorable',
        }),
        colorScale: COLOR_UNFAVORABLE,
        colorScaleAlternate: COLOR_UNFAVORABLE_ALTERNATE,
        colorScaleDelta: COLOR_DELTA_FROM_ALL,
        colorScaleDeltaAlternate: COLOR_DELTA_FROM_ALL_ALTERNATE,
        ratingScale: LIKERT_SCALE,
        ratingScaleAlternate: LIKERT_SCALE_ALTERNATE,
        formatValue: FORMAT_VALUE_PERCENTAGES_UNFAVORABLE,
        unit: '%',
      },
      {
        id: 'neutral_percentage',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.likert_visualization.neutral_percentage',
          defaultMessage: '% Neutral',
        }),
        colorScale: COLOR_NEUTRAL,
        colorScaleDelta: COLOR_DELTA_FROM_ALL,
        colorScaleDeltaAlternate: COLOR_DELTA_FROM_ALL_ALTERNATE,
        ratingScale: LIKERT_SCALE,
        ratingScaleAlternate: LIKERT_SCALE_ALTERNATE,
        formatValue: FORMAT_VALUE_PERCENTAGES_NEUTRAL,
        unit: '%',
      },
      {
        id: 'average',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.likert_visualization.average',
          defaultMessage: 'Average',
        }),
        colorScale: undefined,
        colorScaleAlternate: COLOR_SCALE_ALTERNATE,
        colorScaleDelta: undefined,
        ratingScale: undefined,
        ratingScaleAlternate: LIKERT_SCALE_ALTERNATE,
        formatValue: undefined,
        unit: undefined,
      },
    ],
    [formatMessage]
  );

  const HEATMAP_MODES = useMemo(
    () => [
      {
        id: 'delta',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.heatmap_mode.delta',
          defaultMessage: 'Delta to average',
        }),
      },
      {
        id: 'absolute',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.heatmap_mode.absolute',
          defaultMessage: 'Absolute values',
        }),
      },
    ],
    [formatMessage]
  );

  const ALL_COMPARISON_MODES = useMemo(
    () => [
      {
        id: AllComparisonMode.Report,
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.all_comparison_mode.report',
          defaultMessage: 'This report',
        }),
      },
      {
        id: AllComparisonMode.Organization,
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.all_comparison_mode.organization',
          defaultMessage: 'Full organization',
        }),
      },
    ],
    [formatMessage]
  );

  const HEATMAP_COLOR = useMemo(
    () => [
      {
        id: 'default',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.heatmap_color.default',
          defaultMessage: 'Default',
        }),
      },
      {
        id: 'alternate',
        name: formatMessage({
          id: 'app.views.engagement_survey.engagement_survey.heatmap_color.alternate',
          defaultMessage: 'Accessible',
        }),
      },
    ],
    [formatMessage]
  );
  const isCustomReport = props.reportItemId != null;

  const [selectedVisualization, setSelectedVisualization] =
    React.useState<string>(LIKERT_HEATMAP_VISUALIZATIONS[0].id);

  const [selectedLikertMode, setSelectedLikertMode] = React.useState<string>(
    HEATMAP_MODES[0].id
  );
  const [selectedLikertColor, setSelectedLikertColor] = React.useState<string>(
    HEATMAP_COLOR[0].id
  );

  const [selectedAllComparisonMode, setSelectedAllComparisonMode] =
    React.useState<AllComparisonMode>(ALL_COMPARISON_MODES[0].id);

  const allowDelta = selectedVisualization !== 'average';

  const isNeutralPercentageSelected =
    selectedVisualization === 'neutral_percentage';

  const isDeltaMode = selectedLikertMode === 'delta' && allowDelta;

  const isAlternateColor = selectedLikertColor === 'alternate';

  const visualizationItem = useMemo(() => {
    return LIKERT_HEATMAP_VISUALIZATIONS.find(
      (v) => v.id === selectedVisualization
    );
  }, [LIKERT_HEATMAP_VISUALIZATIONS, selectedVisualization]);

  const onVisualizationChangeHandler = useCallback(
    (e) => {
      setSelectedVisualization(e.target.value);
    },
    [setSelectedVisualization]
  );

  const onLikertModeChangeHandler = useCallback(
    (e) => {
      setSelectedLikertMode(e.target.value);
    },
    [setSelectedLikertMode]
  );

  const onLikertColorChangeHandler = useCallback(
    (e) => {
      setSelectedLikertColor(e.target.value);
    },
    [setSelectedLikertColor]
  );

  const onAllComparisonModehangeHandler = useCallback(
    (e) => {
      setSelectedAllComparisonMode(e.target.value);
    },
    [setSelectedAllComparisonMode]
  );

  const additionalInputs = useMemo(() => {
    return [
      {
        label: (
          <FormattedMessage
            id="app.views.engagement_survey.engagement_survey.likert_metric"
            defaultMessage="Metric"
          />
        ),
        element: (
          <ObjectsDropdown
            objects={LIKERT_HEATMAP_VISUALIZATIONS}
            value={selectedVisualization}
            size={'sm'}
            className="d-inline"
            onChange={onVisualizationChangeHandler}
          />
        ),
      },
      ...(allowDelta
        ? [
            {
              label: (
                <FormattedMessage
                  id="app.views.engagement_survey.engagement_survey.likert_mode"
                  defaultMessage="Mode"
                />
              ),
              element: (
                <ObjectsDropdown
                  objects={HEATMAP_MODES}
                  value={selectedLikertMode}
                  size={'sm'}
                  className="d-inline"
                  onChange={onLikertModeChangeHandler}
                />
              ),
            },
          ]
        : []),
      ...(!(isNeutralPercentageSelected && !isDeltaMode)
        ? [
            {
              label: (
                <FormattedMessage
                  id="app.views.engagement_survey.engagement_survey.likert_color"
                  defaultMessage="Color"
                />
              ),
              element: (
                <ObjectsDropdown
                  objects={HEATMAP_COLOR}
                  value={selectedLikertColor}
                  size={'sm'}
                  className="d-inline"
                  onChange={onLikertColorChangeHandler}
                />
              ),
            },
          ]
        : []),
      ...(isCustomReport
        ? [
            {
              label: (
                <FormattedMessage
                  id="app.views.engagement_survey.engagement_survey.all_comparison"
                  defaultMessage="Average"
                />
              ),
              element: (
                <ObjectsDropdown
                  objects={ALL_COMPARISON_MODES}
                  value={selectedAllComparisonMode}
                  size={'sm'}
                  className="d-inline"
                  onChange={onAllComparisonModehangeHandler}
                />
              ),
            },
          ]
        : []),
    ];
  }, [
    LIKERT_HEATMAP_VISUALIZATIONS,
    HEATMAP_MODES,
    HEATMAP_COLOR,
    ALL_COMPARISON_MODES,
    selectedVisualization,
    selectedAllComparisonMode,
    onVisualizationChangeHandler,
    selectedLikertMode,
    selectedLikertColor,
    onLikertModeChangeHandler,
    onLikertColorChangeHandler,
    isNeutralPercentageSelected,
    allowDelta,
    isDeltaMode,
    isCustomReport,
    onAllComparisonModehangeHandler,
  ]);

  return (
    <LikertHeatmap
      data={props.data}
      renderItem={props.renderItem}
      colorScale={
        isAlternateColor
          ? (visualizationItem?.colorScaleAlternate ??
            visualizationItem?.colorScale)
          : visualizationItem?.colorScale
      }
      colorScaleUnfrozen={
        isDeltaMode
          ? isAlternateColor
            ? (visualizationItem?.colorScaleDeltaAlternate ??
              visualizationItem?.colorScaleDelta)
            : visualizationItem?.colorScaleDelta
          : undefined
      }
      ratingScale={
        isAlternateColor
          ? visualizationItem?.ratingScaleAlternate
          : visualizationItem?.ratingScale
      }
      formatValue={visualizationItem?.formatValue}
      unit={visualizationItem?.unit}
      additionalInputs={additionalInputs}
      allComparisonMode={
        isCustomReport
          ? selectedAllComparisonMode
          : AllComparisonMode.Organization
      }
    />
  );
};

export default React.memo(LikertHeatmapVisualizations);
